<template>
  <form-group
    class="color-input uk-display-inline"
    :label="label"
    :errors="internalErrors"
    @focus="focus"
  >
    <div class="uk-inline">
      <span class="uk-form-icon">
        <color-preview
          :code="code"
          :colorful="colorful"
          class="color-input__preview"
          @click.native="$refs.dropdown.toggle()"
        />
      </span>
      <input
        ref="primaryInput"
        type="text"
        class="uk-input"
        :class="inputCss"
        v-model="name"
        :placeholder="placeholder"
        :disabled="disabled"
        @input="onInput()"
      >
    </div>
    <div class="uk-inline">
      <dropdown-button ref="dropdown">
        <icon slot="button" name="paint-bucket" />

        <div class="uk-form-controls uk-form-controls-text">
          <label class="uk-form-label">
            <input
              type="radio"
              class="uk-radio"
              v-model="pickerMode"
              value="compact"
            >
            {{$t('compact')}}
          </label>
          <label class="uk-form-label">
            <input
              type="radio"
              class="uk-radio"
              v-model="pickerMode"
              value="chrome"
            >
            {{$t('chrome')}}
          </label>
          <label class="uk-form-label">
            <input
              type="radio"
              class="uk-radio"
              v-model="pickerMode"
              value="colorful"
            >
            {{$t('colorful')}}
          </label>
        </div>
        <component
          v-if="showColorPicker"
          class="color-input__picker uk-margin-top"
          :is="pickerMode"
          :value="pickerData"
          :palette="colorPalette"
          @input="onUpdateColorCode"
        />
      </dropdown-button>
    </div>
  </form-group>
</template>

<script>
import includes from 'lodash/includes';
import ColorPreview from '@/components/ColorPreview';
import CommonButton from '@/components/CommonButton';
import DropdownButton from '@/components/DropdownButton';
import ComplexInput from '@/mixins/ComplexInput';
import i18n from '@/i18n';
import { Chrome, Compact } from 'vue-color';

export default {
  name: 'color-input',
  mixins: [ComplexInput],
  components: {
    ColorPreview,
    Compact,
    Chrome,
    CommonButton,
    DropdownButton,
  },
  props: {
    placeholder: String,
  },
  data() {
    const value = this.value || {};
    const code = value.code;
    const colorful = value.colorful || false;
    const colorNames = i18n.messages[i18n.locale].colors;
    const colorPalette = Object.keys(colorNames);

    let pickerMode;
    if (colorful) {
      pickerMode = 'colorful';
    } else if (!code || includes(colorPalette, code)) {
      pickerMode = 'compact';
    } else {
      pickerMode = 'chrome';
    }

    return {
      name: value.name,
      code,
      colorNames,
      colorPalette,
      colorful,
      pickerMode,
      pickerData: { hex: code },
    };
  },
  watch: {
    value(value) {
      const newValue = value || {};
      this.name = newValue.name;
      this.pickerData = { hex: newValue.code };
    },
    pickerMode(mode) {
      this.colorful = mode === 'colorful';
      if (this.colorful) {
        this.setNameFromColorCode();
        this.hideDropdownAndFocusInput();
      }
      this.onInput();
    },
  },
  computed: {
    showColorPicker() {
      return this.pickerMode !== 'colorful';
    },
  },
  methods: {
    onInput() {
      let payload = null;
      if (this.name) {
        payload = { name: this.name };
        if (this.colorful) {
          payload.colorful = true;
          payload.code = null;
        } else {
          payload.colorful = false;
          payload.code = this.code;
        }
      }
      this.$emit('input', payload);
      this.clearErrors();
    },
    onUpdateColorCode(pickerData) {
      this.pickerData = pickerData;
      this.colorful = false;
      this.code = pickerData.hex;
      this.setNameFromColorCode();
      if (this.pickerMode === 'compact') {
        this.hideDropdownAndFocusInput();
      }
      this.onInput();
    },
    setNameFromColorCode() {
      if (this.colorful) {
        this.name = this.$t('colorfulText');
      } else if (this.code) {
        const name = this.colorNames[this.code];
        if (name) this.name = name;
      }
    },
    hideDropdownAndFocusInput() {
      this.$refs.dropdown.hide();
      this.$refs.primaryInput.focus();
    },
  },
  i18n: {
    messages: {
      de: {
        chrome: 'Benutzerdefiniert',
        compact: 'Vorauswahl',
        colorful: 'Mehrfarbig',
        colorfulText: 'Bunt',
        fields: {
          code: 'Code',
          name: 'Name',
        },
      },
    },
  },
};
</script>

<style scoped lang="scss">
.color-input__picker {
  box-shadow: none;
  padding: 0;
}
</style>



// WEBPACK FOOTER //
// ColorInput.vue?05911121